import { useEffect, useState, type FormEvent } from 'react'
import { ApiError, api, can, tierLabel, tierRuleName, type Contact, type InviteResult, type Vendor } from '../lib/api'
import { ErrorBox, Loading } from '../components/DataTable'
import { InviteSent } from '../components/InviteSent'
import { mailWent } from '../lib/mail'
import { myWorkChanged } from '../lib/myWork'
import { rememberSent } from '../lib/sentNote'
import { finishAt } from '../lib/finish'
import { Toggletip } from '../components/kit'
import { DateRuleNote, DateWarnNote } from '../components/DateRuleNote'
import { checkDueAfterToday, dueWarning, todayIndia, useLeftFields } from '../lib/dateRules'
import { openRequestDrawer } from '../lib/requestDrawer'
import { dueDiligenceRefusal, needsReason, wayForward } from '../lib/requestRules'

// Send the questionnaire: screen two of onboarding, and the same screen a
// vendor's record opens when somebody presses Send questionnaire later. One
// decision (which questionnaire, by when), one button, and a plain answer.
//
// # NOT YET, AND WHY (RELEASE 3.3)
//
// The server refuses a questionnaire for a vendor whose request is not
// approved, one awaiting approval, and a rejected or offboarded one, and a
// suspended one without a reason (domain.CheckDueDiligence). A form whose Send
// the server refuses is a dead button, so for those the screen says why, in the
// server's words (lib/requestRules.ts mirrors the rule), and offers the way
// forward instead: approve the request, wait for the lead who will, or open
// the approval. A suspended vendor gets the form with a reason to give.

type Loaded = {
  vendor: Vendor
  contacts: Contact[]
  questionnaires: { code: string; name: string }[]
  tierQuestionnaire: string
}

function plusDays(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() + n)
  return d.toISOString().slice(0, 10)
}

function vendorName(v: Vendor): string {
  return v.displayName?.trim() || v.legalName
}

export function VendorInvite({ vendorId }: { vendorId: string }) {
  const [data, setData] = useState<Loaded | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [questionnaire, setQuestionnaire] = useState('')
  const [dueOn, setDueOn] = useState(plusDays(14))
  const [busy, setBusy] = useState(false)
  const [sent, setSent] = useState<InviteResult | null>(null)
  const [refusal, setRefusal] = useState<{ code: string; message: string; remedy?: string; reference?: string } | null>(null)
  const [contact, setContact] = useState({ fullName: '', email: '' })
  const [contactBusy, setContactBusy] = useState(false)
  const [reload, setReload] = useState(0)
  const [reason, setReason] = useState('')
  // D11 of the date rules, on the field once the person leaves it.
  const leftDue = useLeftFields()
  const dueRule = checkDueAfterToday('Answer-by date', dueOn, todayIndia())

  useEffect(() => {
    let live = true
    Promise.all([
      api.vendor(vendorId),
      api.contacts(vendorId).catch(() => ({ contacts: [] as Contact[], count: 0, reachable: 0, note: '' })),
      api.questionnaires().catch(() => ({ questionnaires: [] })),
      api.tierProfiles().catch(() => ({ tierProfiles: [], note: '' })),
    ])
      .then(([v, c, q, tp]) => {
        if (!live) return
        const profiles = (tp.tierProfiles ?? []) as { tier?: number; questionnaireCode?: string | null }[]
        const mine = profiles.find((p) => p.tier === v.inherentTier)
        // Only a questionnaire with a published version can be sent. A draft
        // offered here is refused by the send, so it is not offered.
        const published = (q.questionnaires ?? []).filter((x) => x.publishedVersionId).map((x) => ({ code: x.code, name: x.name }))
        const tierQ = published.some((x) => x.code === mine?.questionnaireCode) ? (mine?.questionnaireCode ?? '') : ''
        setData({
          vendor: v,
          contacts: c.contacts ?? [],
          questionnaires: published,
          tierQuestionnaire: tierQ,
        })
        setQuestionnaire((cur) => cur || tierQ || (published[0]?.code ?? ''))
      })
      .catch((err) => {
        if (live) setError(err)
      })
    return () => {
      live = false
    }
  }, [vendorId, reload])

  if (error != null) return <ErrorBox error={error} what="This vendor" />
  if (!data) return <Loading what="the vendor" />

  const v = data.vendor
  const recipient = data.contacts.find((c) => c.wouldBeInvited) ?? data.contacts.find((c) => c.isActive && c.email)
  // The rule's short name, not its reasoning. The screen used to read a
  // tierDecision.reason the API never sends (it sends why), so no reason ever
  // showed; the name is what the review asked for in its place.
  const tierText = v.tier?.label || (v.inherentTier ? tierLabel(v.inherentTier) : 'Not yet classified')
  const rule = v.tier?.recorded ? tierRuleName(v.tier.why, v.tier.ruleKey) : ''

  async function send(ev: FormEvent) {
    ev.preventDefault()
    if (dueRule) {
      leftDue.leave('dueOn')
      return
    }
    setBusy(true)
    setRefusal(null)
    try {
      const r = await api.inviteVendor(vendorId, { questionnaireCode: questionnaire || undefined, dueOn, reason: reason.trim() || undefined })
      // An invitation that went lands the person back on the vendor, with the
      // confirmation at the top and the next step under it, rather than on a
      // page whose button went to Home (release 3.2, removal item 6). One that
      // did not go keeps the page below, which says what to do.
      if (r.status === 'ISSUED' && mailWent(r)) {
        rememberSent(vendorId, r)
        myWorkChanged()
        // The send screen is finished: Back from the vendor goes to wherever
        // the person was before it, never to a form ready to send again (B9).
        finishAt('#/vendor/' + vendorId)
        return
      }
      setSent(r)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = (err.body ?? {}) as { message?: string; remedy?: string; reference?: string }
        setRefusal({ code: err.code, message: body.message || err.message, remedy: body.remedy || err.sentRemedy, reference: body.reference })
      } else {
        setRefusal({ code: 'UNKNOWN', message: err instanceof Error ? err.message : String(err) })
      }
    } finally {
      setBusy(false)
    }
  }

  async function addContact(ev: FormEvent) {
    ev.preventDefault()
    setContactBusy(true)
    try {
      await api.createContact(vendorId, { role: 'PRIMARY', fullName: contact.fullName.trim(), email: contact.email.trim(), purpose: 'ASSESSMENT' })
      setRefusal(null)
      setReload((n) => n + 1)
    } catch (err) {
      setRefusal({ code: 'CONTACT', message: err instanceof ApiError ? err.issues.map((i) => i.detail).join(' ') || err.message : String(err) })
    } finally {
      setContactBusy(false)
    }
  }

  // "Sent" only when the API says the invitation will be handed to a carrier.
  // See components/InviteSent.tsx.
  if (sent) return <InviteSent sent={sent} vendorName={vendorName(v)} vendorId={vendorId} />

  // The gate, before the form: a refusal no reason can lift is said in place
  // of the form, with the way forward.
  const askReason = needsReason(v)
  const gate = askReason ? null : dueDiligenceRefusal(v)
  if (gate) {
    const way = wayForward({ ...v, id: vendorId }, gate, can('vendor.request_approve'))
    return (
      <div className="p1-page p1-wizard">
        <div className="p1-head">
          <div>
            <h1>Send the questionnaire</h1>
            <p>{vendorName(v)}, {v.vendorCode}</p>
          </div>
        </div>
        <div className="panel">
          <div className="note stop" role="status">
            <strong>Not yet</strong>
            <p style={{ margin: '4px 0 0' }}>{gate.message}</p>
            {way.kind === 'wait' && <p className="muted" style={{ margin: '4px 0 0' }}>Waiting on {way.who}. They see it on the vendor and in their notices.</p>}
          </div>
          <div className="p1-form-foot">
            {way.kind === 'approve-request' && (
              <a
                className="button primary"
                href={'#/vendor/' + vendorId}
                onClick={() => openRequestDrawer(vendorId)}
              >
                Approve request
              </a>
            )}
            {way.kind === 'open' && (
              <a className="button primary" href={way.href}>
                {way.label}
              </a>
            )}
            <a className="button" href={'#/vendor/' + vendorId}>
              Back to the vendor
            </a>
          </div>
        </div>
      </div>
    )
  }


  return (
    <div className="p1-page p1-wizard">
      <div className="p1-head">
        <div>
          <h1>Send the questionnaire</h1>
          <p>{vendorName(v)}, {v.vendorCode}</p>
        </div>
      </div>
      {/* No step bar: adding a vendor no longer leads here (release 3.3), so
          this is one screen of its own, opened from the vendor's next step. */}

      <form className="panel p1-form" onSubmit={send}>
        <div className="wide">
          <dl className="kv">
            <dt>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Inherent tier
                <Toggletip hover label="About the inherent tier">
                  Set from the answers in step 1. It chooses the questionnaire and how closely the vendor is watched.
                </Toggletip>
              </span>
            </dt>
            <dd>
              {tierText}
              {rule ? <span className="muted"> - {rule}</span> : null}
            </dd>
            <dt>Goes to</dt>
            <dd>
              {recipient ? (
                <>
                  {recipient.fullName || recipient.email} <span className="muted">{recipient.email}</span>
                </>
              ) : (
                <span className="muted">nobody yet, add a contact below</span>
              )}
            </dd>
          </dl>
        </div>

        <div>
          <label htmlFor="vi-q">Questionnaire</label>
          <select id="vi-q" value={questionnaire} onChange={(e) => setQuestionnaire(e.target.value)}>
            {data.questionnaires.map((q) => (
              <option key={q.code} value={q.code}>
                {q.name} {q.code === data.tierQuestionnaire ? '(set for this tier)' : ''}
              </option>
            ))}
          </select>
        </div>
        {askReason && (
          <div className="wide">
            <label htmlFor="vi-reason">Reason</label>
            <textarea
              id="vi-reason"
              rows={2}
              maxLength={1000}
              value={reason}
              aria-describedby="vi-reason-note"
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="sw-help" id="vi-reason-note">
              This vendor is suspended. Say why it is sent due diligence; the reason goes on its record.
            </p>
          </div>
        )}
        <div>
          <label htmlFor="vi-due">Answer by</label>
          <input
            id="vi-due"
            type="date"
            value={dueOn}
            min={plusDays(1)}
            onChange={(e) => setDueOn(e.target.value)}
            onBlur={() => leftDue.leave('dueOn')}
            aria-invalid={leftDue.shown('dueOn', dueRule) ? true : undefined}
            aria-describedby={leftDue.shown('dueOn', dueRule) ? 'vi-due-rule' : undefined}
          />
          <DateRuleNote id="vi-due-rule" message={leftDue.shown('dueOn', dueRule)} />
          <DateWarnNote
            id="vi-due-warn"
            message={leftDue.shown('dueOn', dueRule ? '' : dueWarning(dueOn, data.vendor.contract?.end ?? '', todayIndia()))}
          />
        </div>

        {refusal && refusal.code !== 'NO_CONTACT' && (
          <div className="wide note stop" role="alert">
            <strong>{refusal.code === 'ALREADY_OPEN' ? 'Already sent' : 'Not sent'}</strong>
            <p style={{ margin: '4px 0 0' }}>{refusal.message}</p>
            {refusal.remedy && <p className="muted" style={{ margin: '4px 0 0' }}>{refusal.remedy}</p>}
            {refusal.code === 'ALREADY_OPEN' && (
              <p style={{ margin: '6px 0 0' }}>
                <a href={'#/vendor/' + vendorId}>See the open questionnaire on the vendor</a>
              </p>
            )}
          </div>
        )}

        {(!recipient || refusal?.code === 'NO_CONTACT') && (
          <div className="wide panel" style={{ background: 'var(--surface-2)' }}>
            <strong>Add the person who answers for {vendorName(v)}</strong>
            <div className="p1-form" style={{ marginTop: 8 }}>
              <div>
                <label htmlFor="vi-cname">Name</label>
                <input id="vi-cname" type="text" value={contact.fullName} onChange={(e) => setContact({ ...contact, fullName: e.target.value })} />
              </div>
              <div>
                <label htmlFor="vi-cemail">Email</label>
                <input id="vi-cemail" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
              </div>
              <div className="wide">
                <button
                  type="button"
                  disabled={contactBusy || !contact.email}
                  onClick={addContact}
                  data-action="tprm.vendor-invite.a02"
                >
                  {contactBusy ? 'Saving' : 'Save contact'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="p1-form-foot">
          <button
            type="submit"
            className="primary"
            disabled={busy || !recipient || (askReason && reason.trim() === '')}
            data-action="tprm.vendor-invite.a01"
          >
            {busy ? 'Sending' : 'Send now'}
          </button>
          <a className="button" href={'#/vendor/' + vendorId}>
            Not now
          </a>
        </div>
      </form>
    </div>
  )
}
